import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { cn } from "@/utils/cn";
import type { RosterNurse } from "@vTypes/snapshot-type";

/** 실측 앵커: 1칸 67px, 7칸 512px */
const ASSIGNMENT_BAR_MIN_SPAN = 1;
const ASSIGNMENT_BAR_MIN_WIDTH_PX = 67;
const ASSIGNMENT_BAR_MAX_SPAN = 7;
const ASSIGNMENT_BAR_MAX_WIDTH_PX = 512;

function assignmentBarWidthPx(spanDays: number): number {
  if (spanDays <= ASSIGNMENT_BAR_MIN_SPAN) return ASSIGNMENT_BAR_MIN_WIDTH_PX;
  if (spanDays >= ASSIGNMENT_BAR_MAX_SPAN) return ASSIGNMENT_BAR_MAX_WIDTH_PX;

  // 1칸~7칸 구간을 선형 보간해서 마진/경계 누적 오차를 완화.
  const perGap =
    (ASSIGNMENT_BAR_MAX_WIDTH_PX - ASSIGNMENT_BAR_MIN_WIDTH_PX) /
    (ASSIGNMENT_BAR_MAX_SPAN - ASSIGNMENT_BAR_MIN_SPAN);
  const raw =
    ASSIGNMENT_BAR_MIN_WIDTH_PX + (spanDays - ASSIGNMENT_BAR_MIN_SPAN) * perGap;
  return Math.round(raw);
}

const ASSIGNMENT_BAR_BORDER = "#d1d5db";
const ASSIGNMENT_BAR_TEXT = "#111827";
const ASSIGNMENT_BAR_BACKGROUND = "rgba(255, 255, 255, 0.92)";

function ViewRosterDay({
  day,
  holiday,
  myShift,
  /** 카탈로그 기준 표시용(예: `shift_id`). 없으면 `myShift` 원문 사용 */
  displayShiftId,
  color,
  coworkerList,
  muted,
  assignmentBar,
}: {
  day: number;
  holiday: { weekend: number; isHol: boolean };
  myShift: string | null;
  displayShiftId?: string | null;
  color: string;
  coworkerList: RosterNurse[] | undefined;
  muted?: boolean;
  /** 파견 등 — `w_selectCalendar_date`(시프트 영역) 기준 `absolute` 하단 바 */
  assignmentBar?: {
    label: string;
    color: string;
    spanDays: number;
    muted?: boolean;
  } | null;
}) {
  const normalizedShift = (myShift ?? "").trim();
  const hasShift = normalizedShift !== "" && normalizedShift !== "-";
  const hasColor = !!color && color !== "transparent";
  const label =
    (displayShiftId ?? "").trim() || (hasShift ? normalizedShift : "");
  const contentMutedClass = muted ? "opacity-10 saturate-0" : "";

  return (
    <div
      className={`
                ${holiday.weekend === 6 ? "!text-text-sat" : holiday.weekend === 0 ? "!text-text-sun" : ""}
                ${holiday.isHol && "text-text-holiday"}
               roster-view-calendar__day roster-view-calendar__coworker-wrap !text-text-sub
         ${hasShift && hasColor && "!text-white"}
            w-full min-w-0
            `}
    >
      <div className={cn("roster-view-calendar__date !p-1", contentMutedClass)}>
        {day}
      </div>
      <div
        className={cn(
          "roster-view-calendar__shift text-3xl!",
          !hasShift && "bg-muted! text-accent-foreground/10!",
          contentMutedClass
        )}
        style={{ backgroundColor: color, color: getReadableShiftCodeTextColor(color) }}
      >
        {hasShift ? label : "-"}
        {coworkerList && coworkerList.length > 0 && (
          <div className="roster-view-calendar__coworker-list">
            <ul>
              {coworkerList.map((n, index) => {
                return <li key={index}>{n.name}</li>;
              })}
            </ul>
          </div>
        )}
      </div>
      {assignmentBar != null &&
        assignmentBar.label !== "" &&
        assignmentBar.spanDays >= 1 && (
          <div
            className={cn(
              "pointer-events-none absolute bottom-1 left-1 z-[5] flex h-[18px] min-w-0 max-w-none items-center justify-center overflow-hidden rounded-full border text-center text-[11px] font-semibold leading-none transition-opacity",
              assignmentBar.muted && "opacity-20"
            )}
            style={{
              width: `calc(${assignmentBarWidthPx(assignmentBar.spanDays)}px - 8px)`,
              borderColor: ASSIGNMENT_BAR_BORDER,
              color: ASSIGNMENT_BAR_TEXT,
              backgroundColor: ASSIGNMENT_BAR_BACKGROUND,
            }}
          >
            <span className="block min-w-0 max-w-[80%] truncate px-1 text-center">
              {assignmentBar.label}
            </span>
          </div>
        )}
    </div>
  );
}
export default ViewRosterDay;
